import { HashrateItem } from "@components/Charts/ChartDatasetUtils";
import useChartLiveDataSource, { IChartLiveDataSource } from "@components/Charts/ChartLiveDataSource";
import { ChartScales, CleanChartDataset, NullableChartPeriod } from "@components/Charts/CleanChart.control";
import useChartPeriodControl, { IChartPeriodControl } from "@components/Charts/CleanChartPeriod.control";
import { getRejectRate } from "@components/Charts/HashChart";
import { ISubscription, LiveValueListener, SocketContext } from "@components/Context/SocketContext";
import { IStatisticControl } from "@components/Pages/Locations/LocationRow/LocationMore/LocationChart/IStatisticControl";
import { MINER_STATUSES_E } from "@constants/MinerStatuses";
import { RecordResponse } from "@services/http/responses/BaseResponse";
import { MinerBaseEvent, MinerHashEvent, SocketEventTypes, SocketMessage } from "@services/http/socket/responses";
import useApiRequests from "@services/http/useApiRequest";
import { useAuth } from "@utils/hooks/useAuth";
import { useFirstRender } from "@utils/hooks/useFirstRender";
import moment from "moment-timezone";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { getOldOrNewStatistic, getScalePeriodDaysCount, IMinerChartValue, minerEventToCleanChart, ScaleListItem, scales } from "./MinerChart.service";

interface IMinerChartProps {
  minerId: number;
  currentSpeed?: number;
  currentRejected: number;
  currentAccepted: number;
  currentState: number;
}

interface IMinerChartControl extends IStatisticControl {
  periodControl: IChartPeriodControl;
  liveDataSource: IChartLiveDataSource<IMinerChartValue>;
  scales: ScaleListItem[];
  scale: ChartScales | null;
  setScale(value: ChartScales): void;
  refresh(): void;
}

export default function useMinerChartControl({
  minerId,
  currentSpeed,
  currentAccepted,
  currentRejected,
  currentState,
}: IMinerChartProps) {

  const TAG = "MinerChart";
  const {
    addMessageListener,
    removeMessageListener,
  } = useContext(SocketContext);
  const auth = useAuth();
  const requests = useApiRequests();
  const [datasets, setDatasets] = useState<CleanChartDataset[]>([]);
  const [isLoading, setLoading] = useState(false);
  const subscriptionRef = useRef<ISubscription<number> | null>(null);
  const [scale, setScale] = useState<ChartScales | null>(ChartScales.Scale5M);
  const lastValueRef = useRef<{
    currentSpeed?: number;
    currentAccepted: number;
    currentRejected: number;
    currentState: number;
  }>();

  const isFirstRender = useFirstRender();
  //const avgHook = useChartAvgValues();

  useEffect(() => {
    lastValueRef.current = {
      currentSpeed,
      currentAccepted,
      currentRejected,
      currentState
    };
    //avgHook.setListener(chartDataSourceCache.enqueue);
    load(onSocketMessage);
    return () => {
      //avgHook.clearLustener();
      unload(onSocketMessage);
    }
  }, []);

  useEffect(() => {
    //dev.logt("MinerChart", `Current scale ${scale}`);
    if (isFirstRender) return;
    const periodDays = periodControl.getPeriodDaysCount();
    if (periodDays > -1) {
      const scaleDays = getScalePeriodDaysCount(scale ?? ChartScales.Scale5M);
      if (periodDays < scaleDays) {
        periodControl.expandToLeft(scaleDays);
      } else {
        refresh();
      }
    } else {
      refresh();
    }
  }, [scale]);

  // const onTickSecond = useCallback((timeInSec, values) => {
  //   avgHook.onTickSecond(scale, timeInSec, values)
  // }, [scale]);

  const chartDataSourceCache = useChartLiveDataSource<IMinerChartValue>(
    minerEventToCleanChart,
  );

  const periodControl = useChartPeriodControl({
    onPeriodChanged: v => update(v),
  });

  const getMinerStatistic = async (
    period: NullableChartPeriod,
    minerId: number,
    isAdmin: boolean,
    scale: ChartScales | null,
    tag: string
  ): Promise<HashrateItem[]> => { // | number[][]> => {
    setLoading(true);
    const hashrates = await requests.enqueue<RecordResponse<HashrateItem[]>>(
      getOldOrNewStatistic(isAdmin, period, minerId, scale)
    );
    setLoading(false);
    return Promise.resolve(hashrates?.Record || []);
  };

  const update = useCallback(async (
    period: NullableChartPeriod
  ) => {
    const raw = await getMinerStatistic(period, minerId, auth.isAdmin, scale, "update");
    const datasets: CleanChartDataset[] = [];

    if (lastValueRef.current !== undefined) {
      //const lastPeriodDay = periodControl.period[1]?.clone();
      if (//currentSpeed !== undefined && 
        (moment.utc().isBefore(periodControl.period[1], "day") || periodControl.period[1] === null)
      ) {
        raw.push({
          status: lastValueRef.current.currentState,
          speed: lastValueRef.current.currentSpeed ?? 0,
          accepted: lastValueRef.current.currentAccepted,
          rejected: lastValueRef.current.currentRejected,
          timestamp: moment.utc().unix()
        } as any);
        chartDataSourceCache.setLastValue({
          state: lastValueRef.current.currentState,
          speed: lastValueRef.current.currentSpeed ?? 0,
          rejectedRate: getRejectRate(lastValueRef.current.currentRejected, lastValueRef.current.currentAccepted),
        })
      }
    }

    datasets.push({
      label: `miner-${minerId}`,
      data: raw
    });
    setDatasets(datasets);
  }, [scale, periodControl.period]);


  // const appendCurrent = useCallback(() => {
  //   if (lastValueRef.current !== undefined) {
  //     if (//currentSpeed !== undefined && 
  //       (moment().isSame(periodControl.period[1], "day") || periodControl.period[1] === null)
  //     ) {
  //       chartDataSourceCache.enqueue(0  /*created*/,
  //         {
  //           speed: lastValueRef.current.currentSpeed,
  //           rejectedRate: getRejectRate(lastValueRef.current.currentRejected, lastValueRef.current.currentAccepted),
  //         } as IMinerChartValue,
  //         "appendCurrent", minerId
  //       );
  //     }
  //   }
  // }, [periodControl.period]);

  const load = async (listener: LiveValueListener) => {
    await update(periodControl.period);
    addMessageListener(SocketEventTypes.MINER_HASRATE, listener);
    subscriptionRef.current = {
      id: minerId,
      isAlgo: false,
      isTotal: false,
    } as ISubscription<number>;
  }

  const unload = (listener: LiveValueListener) => {
    removeMessageListener(SocketEventTypes.MINER_HASRATE, listener);
    setDatasets([]);
  }

  const onSocketMessage = useCallback((message: SocketMessage<any>) => {
    const data = message.event as MinerHashEvent;
    if (parseInt(data.id ?? "0") === minerId) {
      lastValueRef.current = {
        currentSpeed: data.speed,
        currentAccepted: data.accepted,
        currentRejected: data.rejected,
        currentState: data.state,
      };
      chartDataSourceCache.enqueue(moment().unix(), {
        state: data.state,
        speed: data.speed,
        rejectedRate: getRejectRate(data.rejected, data.accepted),
      } as IMinerChartValue, "onSocketMessage", minerId);
      // if ([
      //   MINER_STATUSES_E.OK,
      //   MINER_STATUSES_E.LOW,
      //   MINER_STATUSES_E.BAD
      // ].indexOf(data.state) === -1) {
      //   avgHook.disable();
      // } else {
      //   avgHook.enable();
      // }
    }
  }, [chartDataSourceCache, periodControl.period]);

  const refresh = useCallback(async () => {
    await update(periodControl.period);
  }, [periodControl.period, scale])

  return {
    isLoading,
    datasets,
    statistic: [0, 0, 0, 0],
    periodControl,
    liveDataSource: chartDataSourceCache,
    scales: scales.filter(s => s.visible),
    scale,
    setScale,
    update,
    load,
    unload,
    refresh,
    //getId,
  }
}